import { currentLanguage, LANGUAGE_HEADER } from "@i18n";
import { ApiError, type Problem } from "./errors";

/** Header the backend requires on unsafe requests made with the session cookie (anti-CSRF, see platform/auth/deps.py). */
const CSRF_HEADER = { "X-Requested-With": "breeder" } as const;
const SAFE = new Set(["GET", "HEAD", "OPTIONS"]);

/** The one place the web client talks HTTP: the CSRF header on writes, the UI language, errors as ApiError (a lost
 *  session → login). */
async function send(url: string, options: RequestInit, accept: string): Promise<Response> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  headers.set("Accept", accept);
  headers.set(LANGUAGE_HEADER, currentLanguage()); // texts the server writes (documents, labels) follow the UI language
  if (!SAFE.has(method)) {
    for (const [k, v] of Object.entries(CSRF_HEADER)) headers.set(k, v);
  }
  const response = await fetch(url, { ...options, method, headers, credentials: "same-origin" });
  if (!response.ok) {
    const body = await response.json().catch(() => null);
    const problem = (body ?? { code: "http_error", status: response.status, detail: response.statusText }) as Problem;
    if (response.status === 401 && problem.code === "not_authenticated") {
      // the browser is leaving for the login page: the request stays pending (the screen keeps loading) rather than
      // failing, so no "session expired" error flashes up before the login page opens
      redirectToLogin();
      return new Promise<never>(() => {});
    }
    throw new ApiError(problem, response.status);
  }
  return response;
}

/** JSON endpoints; Orval's generated client calls it for every endpoint. */
export const apiFetch = async <T>(url: string, options: RequestInit = {}): Promise<T> => {
  const response = await send(url, options, "application/json, application/problem+json");
  return (response.status === 204 ? null : await response.json().catch(() => null)) as T;
};

/** A file from an endpoint and the server's file name for it (or ``fallback``). */
async function fetchFile(url: string, options: RequestInit, fallback: string): Promise<{ blob: Blob; name: string }> {
  const response = await send(url, options, "*/*");
  const disposition = response.headers.get("Content-Disposition") ?? "";
  const encoded = /filename\*=UTF-8''([^;]+)/.exec(disposition)?.[1];
  const name = encoded ? decodeURIComponent(encoded) : (/filename="([^"]+)"/.exec(disposition)?.[1] ?? fallback);
  return { blob: await response.blob(), name };
}

const postJson = (body: unknown): RequestInit => ({
  method: "POST",
  body: JSON.stringify(body),
  headers: { "Content-Type": "application/json" },
});

/** A file from an endpoint (e.g. an .xlsx export): saved under the server's file name, or ``fallback``. */
export async function apiDownload(url: string, options: RequestInit, fallback: string): Promise<void> {
  const { blob, name } = await fetchFile(url, options, fallback);
  saveBlob(blob, name);
}

/** A file a POST of ``body`` (JSON) makes, e.g. an export of what a page shows. */
export function apiDownloadJson(url: string, body: unknown, fallback: string): Promise<void> {
  return apiDownload(url, postJson(body), fallback);
}

// how long an opened file stays readable in its tab (the browser's viewer has read it by then)
const OPENED_FOR_MS = 5 * 60_000;

/** A new tab for a document still to come (a PDF to print), opened at the click: a tab opened later is blocked as a
 *  pop-up. It says ``waiting`` meanwhile; null when the browser blocks it anyway. */
export function openTab(waiting: string): Window | null {
  const tab = window.open("", "_blank");
  if (tab) {
    tab.document.title = waiting;
    tab.document.body.textContent = waiting;
  }
  return tab;
}

/** The document a POST of ``body`` (JSON) makes, shown in ``tab`` (from ``openTab``), where the browser's viewer
 *  prints it; saved as a file when there is no tab. The tab closes when the document cannot be made. */
export async function apiShowJson(tab: Window | null, url: string, body: unknown, fallback: string): Promise<void> {
  try {
    const { blob, name } = await fetchFile(url, postJson(body), fallback);
    if (!tab || tab.closed) return saveBlob(blob, name);
    const href = URL.createObjectURL(blob);
    tab.location.href = href;
    setTimeout(() => URL.revokeObjectURL(href), OPENED_FOR_MS);
  } catch (error) {
    tab?.close();
    throw error;
  }
}

/** Hand a file made in the browser (or downloaded) to the user. */
export function saveBlob(blob: Blob, name: string): void {
  const href = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = href;
  a.download = name;
  a.click();
  URL.revokeObjectURL(href);
}

export function redirectToLogin(): void {
  const here = `${window.location.pathname}${window.location.search}`;
  // the Keycloak pages open in the language this browser last used (the server reads its language cookie)
  window.location.assign(`/bff/login?return_to=${encodeURIComponent(here)}`);
}
